import { http, publicHttp } from "../Guard";
import {
  AuthInput,
  AuthToken,
  RefreshToken,
  RefreshTokenInput,
  RegisterCompanyUser,
  RegisterCompanyUserInput,
  RegisterUserInput,
  ResetPasswordCodeInput,
  ResetPasswordInput,
  User,
  UserInput,
} from "./auth.default";
import {
  RegisterCompanyUserFileInput,
  RegisterCompanyUsersPreview,
  RegisterFilePreviewInput,
} from "./data.default";

export function authLogin(input: AuthInput): Promise<AuthToken> {
  return http.post("/api/Auth/Login", input).then(({ data }) => data);
}

export function authLogout(): Promise<void> {
  return http.post("/api/Auth/Logout").then(({ data }) => data);
}

export function getProfile(): Promise<User> {
  return http.get("/api/Auth/Profile").then(({ data }) => data);
}

export function updateProfile(input: UserInput): Promise<void> {
  return http.post("/api/Auth/Profile", input);
}

export function getAvatar(): Promise<string> {
  return http
    .get("/api/Auth/Profile/Avatar", {
      responseType: "blob",
    })
    .then(({ data }) => URL.createObjectURL(data));
}

export function uploadAvatar(file: File): Promise<any> {
  const formData = new FormData();
  formData.append("avatarFile", file);
  return http
    .post("/api/Auth/Profile/Avatar", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    })
    .then(({ data }) => data);
}

export function refreshToken(input: RefreshTokenInput): Promise<RefreshToken> {
  return publicHttp
    .post("/api/Auth/RefreshToken", input, {
      headers: {
        Authorization: input.token,
      },
    })
    .then(({ data }) => data);
}

export function registerUser(input: RegisterUserInput): Promise<void> {
  return http.post("/api/Auth/Register", input);
}

export function registerCompanyUser(
  input: RegisterCompanyUserInput,
): Promise<RegisterCompanyUser> {
  return http
    .post("/api/Auth/RegisterForCompany", input)
    .then(({ data }) => data);
}

export function registerCompanyUsersFile(
  input: RegisterCompanyUserFileInput,
): Promise<RegisterCompanyUser> {
  const formData = new FormData();
  formData.append("xmlFile", input.file);
  return http
    .post("/api/Auth/RegisterForCompanyWithFile", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
      params: {
        companyUid: input.companyUid,
      },
    })
    .then(({ data }) => data);
}

export function resetPasswordCode(
  input: ResetPasswordCodeInput,
): Promise<string> {
  return http
    .get("/api/Auth/PasswordResetCode", {
      params: input,
    })
    .then(({ data }) => data);
}

export function resetPassword(input: ResetPasswordInput) {
  return http.post("/api/Auth/ResetPassword", input).then(({ data }) => data);
}

export function getRegisterUsersTemplate() {
  return http
    .get("/api/Auth/RegisterForCompanyWithFile/Template", {
      responseType: "blob",
    })
    .then(({ data }) => {
      try {
        const link = document.createElement("a");
        const urlBlob = window.URL.createObjectURL(new Blob([data]));
        try {
          link.href = urlBlob;
          link.setAttribute("download", "company-register-users-template.xlsx");
          document.body.appendChild(link);
          link.click();
        } catch {
          throw new Error(
            "Failed to download company register users template.",
          );
        } finally {
          link.remove();
          window.URL.revokeObjectURL(urlBlob);
        }
      } catch {
        throw new Error("Failed to download company register users template.");
      }
    });
}

export function registerForCompanyWithFilePreview(
  input: RegisterFilePreviewInput,
): Promise<RegisterCompanyUsersPreview> {
  const formData = new FormData();
  formData.append("xmlFile", input.file);
  return http
    .post("/api/Auth/RegisterForCompanyWithFilePreview", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
      params: {
        companyUid: input.companyUid,
      },
    })
    .then(({ data }) => data);
}

// Company Profile Page

export function getCompanyLogo(cmpUid: string): Promise<string> {
  return http
    .get(`/api/Auth/CompanyProfile/${cmpUid}/Logo`, {
      responseType: "blob",
    })
    .then(({ data }) => URL.createObjectURL(data));
}

export function uploadCompanyLogo(file: File, cmpUid: string): Promise<any> {
  const formData = new FormData();
  formData.append("logoFile", file);
  return http
    .post(`/api/Auth/CompanyProfile/${cmpUid}/Logo`, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    })
    .then(({ data }) => data);
}
